import { useTablePageController } from './useTablePageController'
import { TableCard } from './components/TableCard'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import { useQueryClient } from '@tanstack/react-query'

import { cn } from '@/app/utils/cn'
import { useWindowSize } from './useWindowSize'
import { updateTable } from '@/app/services/tableService/updateTable'

export const TablePage = () => {
  const { data, params } = useTablePageController()
  const queryClient = useQueryClient()
  const { width } = useWindowSize()

  const getColumnsPerRow = (screenWidth: number) => {
    if (screenWidth < 640) return 1
    if (screenWidth < 1024) return 2
    return 3
  }

  const columnsPerRow = getColumnsPerRow(width)

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !data) return

    const sourceRowIndex = parseInt(result.source.droppableId.split('-')[1])
    const destinationRowIndex = parseInt(
      result.destination.droppableId.split('-')[1],
    )
    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    const groupedTables = data.pages.flatMap((page) =>
      page.tables.reduce(
        (rows, table, index) => {
          const rowIndex = Math.floor(index / columnsPerRow)
          if (!rows[rowIndex]) rows[rowIndex] = []
          rows[rowIndex].push(table)
          return rows
        },
        [] as Array<typeof page.tables>,
      ),
    )

    const [movedTable] = groupedTables[sourceRowIndex].splice(sourceIndex, 1)
    groupedTables[destinationRowIndex].splice(destinationIndex, 0, movedTable)

    const updatedTables = groupedTables.flat()
    const order = updatedTables.findIndex(t => t._id == movedTable._id)

    await updateTable({ ...movedTable, order }, movedTable._id)

    queryClient.invalidateQueries({ queryKey: ['allTables', params] })
  }


  return (
    <div>
      <div className="space-y-4">
        <DragDropContext onDragEnd={handleDragEnd}>
          {data &&
            data.pages
              .flatMap((page) =>
                page.tables.reduce(
                  (rows, table, index) => {
                    const rowIndex = Math.floor(index / columnsPerRow)
                    if (!rows[rowIndex]) rows[rowIndex] = []
                    rows[rowIndex].push(table)
                    return rows
                  },
                  [] as Array<typeof page.tables>,
                ),
              )
              .map((row, rowIndex) => (
                <Droppable
                  key={`row-${rowIndex}`}
                  droppableId={`row-${rowIndex}`}
                  direction="horizontal"
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cn(
                        'grid gap-4',
                        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                        snapshot.isDraggingOver ? 'rounded-lg bg-gray-50' : '',
                      )}
                    >
                      {row.map((table, index) => (
                        <Draggable
                          key={table._id}
                          draggableId={table._id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={cn(
                                'transition-all duration-200',
                                snapshot.isDragging
                                  ? 'z-50 scale-105 shadow-lg'
                                  : '',
                                'touch-manipulation',
                              )}
                              style={{
                                ...provided.draggableProps.style,
                              }}
                            >
                              <TableCard {...table} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}
        </DragDropContext>
      </div>
    </div>
  )
}
